"use client";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ArrowRight, FileUp, Plus, Trash2 } from "lucide-react";
import { Banner, Button, Fact, Facts, Field, FieldGrid, Input, Textarea } from ".";
import { AccessAction } from "./access-action";
import { Choice, ReadState, useRead, type PageResult } from "./record-tools";
import {
  BATCH_LABEL,
  FIELD_LABEL,
  batchCounts,
  csvHeader,
  useImportCommand,
  type Batch,
  type ImportTarget,
  type Mapping,
  type Report, KIND_HINT } from "./import-model";
import { api, ApiError, formatTime } from "../../lib/api";
export function ImportSteps({ step }: { step: number }) {
  return (
    <ol className="import-steps" aria-label="导入办理进度">
      {["文件与字段", "预检对账", "确认入库"].map((label, i) => (
        <li key={label} aria-current={step === i + 1 ? "step" : undefined} data-done={step > i + 1}>
          <span>{i + 1}</span>
          <strong>{label}</strong>
          {i < 2 && <ArrowRight size={15} />}
        </li>
      ))}
    </ol>
  );
}
export function MappingEditor({
  onCancel,
  onSaved
}: {
  onCancel: () => void;
  onSaved: (result: unknown) => void;
}) {
  const targets = useRead<{ items: ImportTarget[] }>("/api/v1/member-imports/targets"),
    send = useImportCommand();
  const [name, setName] = useState(""),
    [source, setSource] = useState(""),
    [pairs, setPairs] = useState([{ column: "", fieldId: "" }]);
  const columns = pairs.map((p) => p.column.trim()),
    used = pairs.map((p) => p.fieldId),
    missing = targets.data?.items.filter((t) => t.required && !used.includes(t.fieldId)) ?? [];
  const duplicate =
    columns.some((c, i) => !!c && columns.indexOf(c) !== i) ||
    used.some((c, i) => !!c && used.indexOf(c) !== i);
  return (
    <AccessAction
      eyebrow="会员数据导入 · 字段对应"
      title="登记字段对应"
      description="把源文件中的表头与可导入字段一一对应，保存后可重复使用。"
      dirty={!!name || !!source || pairs.some((p) => p.column || p.fieldId)}
      disabled={!targets.data || !!missing.length || duplicate}
      label="保存字段对应"
      onCancel={onCancel}
      onSaved={onSaved}
      inspect={async () => {
        const r = await api<{ items: Mapping[] }>("/api/v1/member-imports/mappings");
        return r.items
          .filter((m) => m.name === name.trim())
          .map((m) => `${m.name} · ${m.sourceRef} · ${Object.keys(m.columns).length} 列`);
      }}
      submit={() =>
        send("/api/v1/member-imports/mappings", {
          name: name.trim(),
          sourceRef: source.trim(),
          columns: Object.fromEntries(pairs.map((p) => [p.column.trim(), p.fieldId]))
        })
      }
    >
      <section className="record-section">
        <h2>对应规则</h2>
        <FieldGrid>
          <Field label="规则名称">
            <Input
              required
              minLength={2}
              maxLength={60}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：支部报送花名册"
            />
          </Field>
          <Field label="来源依据">
            <Input
              required
              minLength={2}
              maxLength={200}
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="填写表单名称与版本"
            />
          </Field>
        </FieldGrid>
      </section>
      <section className="record-section">
        <h2>文件表头与导入字段</h2>
        <ReadState {...targets} retry={targets.reload} />
        <p className="record-muted">表头需与文件第一行一致。姓名、手机号、所属组织为必填对应项。</p>
        {pairs.map((pair, i) => (
          <div className="import-pair" key={i}>
            <Field label={`第 ${i + 1} 列表头`}>
              <Input
                required
                value={pair.column}
                onChange={(e) =>
                  setPairs((old) =>
                    old.map((p, j) => (i === j ? { ...p, column: e.target.value } : p))
                  )
                }
                placeholder="例如：姓名"
              />
            </Field>
            <ArrowRight size={16} />
            <Choice
              label={`第 ${i + 1} 列导入字段`}
              required
              value={pair.fieldId}
              disabled={!targets.data}
              onChange={(value) =>
                setPairs((old) => old.map((p, j) => (i === j ? { ...p, fieldId: value } : p)))
              }
              options={(targets.data?.items ?? []).map((t) => ({
                value: t.fieldId,
                label: t.label + (t.required ? "（必填）" : "") + (KIND_HINT[t.kind] ? `（${KIND_HINT[t.kind]}）` : "")
              }))}
            />
            <Button
              type="button"
              variant="ghost"
              aria-label={`移除第 ${i + 1} 列`}
              disabled={pairs.length === 1}
              onClick={() => setPairs(pairs.filter((_, j) => i !== j))}
            >
              <Trash2 size={16} />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={pairs.length >= 60}
          onClick={() => setPairs([...pairs, { column: "", fieldId: "" }])}
        >
          <Plus size={15} />
          添加一列
        </Button>
        {duplicate && <Banner tone="danger">表头与导入字段都不能重复，请核对对应关系。</Banner>}
        {targets.data && missing.length > 0 && (
          <p className="import-validation">还需对应：{missing.map((t) => t.label).join("、")}。</p>
        )}
      </section>
    </AccessAction>
  );
}
export function StageEditor({
  onCancel,
  onSaved
}: {
  onCancel: () => void;
  onSaved: (result: unknown) => void;
}) {
  const mappings = useRead<{ items: Mapping[] }>("/api/v1/member-imports/mappings");
  const [mappingId, setMappingId] = useState(""),
    [source, setSource] = useState(""),
    [reason, setReason] = useState("");
  const [fileName, setFileName] = useState(""),
    [content, setContent] = useState(""),
    [header, setHeader] = useState<string[]>([]),
    [fileError, setFileError] = useState(""),
    [reading, setReading] = useState(false);
  const ticket = useRef(0),
    send = useImportCommand();
  useEffect(
    () => () => {
      ticket.current++;
    },
    []
  );
  const mapping = mappings.data?.items.find((m) => m.id === mappingId),
    missing = Object.keys(mapping?.columns ?? {}).filter((c) => !header.includes(c));
  const dirty = !!(mappingId || source || reason || fileName);
  return (
    <AccessAction
      eyebrow="会员数据导入 · 新建批次"
      title="新建导入"
      description="准备 CSV 文件，选择字段对应规则，再生成逐行对账报告。"
      dirty={dirty}
      disabled={reading || !content || !mapping || !!missing.length}
      label="生成预检报告"
      onCancel={onCancel}
      onSaved={onSaved}
      inspect={async () => {
        const r = await api<PageResult<Batch>>(
          `/api/v1/member-imports?${new URLSearchParams({ q: source.trim(), limit: "20" })}`
        );
        return r.items.map(
          (b) =>
            `${b.sourceRef} · ${b.mappingName} · ${BATCH_LABEL[b.status]} · ${b.rowCount} 行 · ${formatTime(b.recordedAt)}`
        );
      }}
      submit={() =>
        send<Report>("/api/v1/member-imports", {
          mappingId,
          sourceRef: source.trim(),
          reason: reason.trim(),
          content
        })
      }
    >
      <ImportSteps step={1} />
      <section className="record-section">
        <h2>选择文件</h2>
        <Field label="CSV 文件" hint="UTF-8 编码，第一行为表头，最多 2000 行、1 MiB。">
          <input
            className="import-file"
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={async (e) => {
              const file = e.target.files?.[0],
                current = ++ticket.current;
              setContent("");
              setHeader([]);
              setFileError("");
              setFileName(file?.name ?? "");
              if (!file) {
                setReading(false);
                return;
              }
              setReading(true);
              try {
                if (file.size > 1024 * 1024) throw Error("文件超过 1 MiB，请拆分后再导入。");
                const text = new TextDecoder("utf-8", { fatal: true }).decode(
                  await file.arrayBuffer()
                );
                const cols = csvHeader(text);
                if (current === ticket.current) {
                  setContent(text);
                  setHeader(cols);
                }
              } catch (e) {
                if (current === ticket.current)
                  setFileError(
                    e instanceof TypeError
                      ? "无法按 UTF-8 读取文件，请另存为 UTF-8 CSV 后重试。"
                      : e instanceof Error
                        ? e.message
                        : "文件未读出，请重新选择。"
                  );
              } finally {
                if (current === ticket.current) setReading(false);
              }
            }}
          />
        </Field>
        {reading ? (
          <p role="status">正在读取文件…</p>
        ) : fileError ? (
          <Banner tone="danger">{fileError}</Banner>
        ) : content ? (
          <div className="import-context">
            <FileUp size={19} />
            <span>
              {fileName} · 已读取 {header.length} 个表头
            </span>
          </div>
        ) : null}
      </section>
      <section className="record-section">
        <h2>核对字段对应</h2>
        <ReadState {...mappings} retry={mappings.reload} />
        <Choice
          label="字段对应规则"
          required
          value={mappingId}
          onChange={setMappingId}
          disabled={!mappings.data}
          options={(mappings.data?.items ?? []).map((m) => ({ value: m.id, label: m.name }))}
        />
        {mappings.data && !mappings.data.items.length && (
          <p>
            尚未登记字段对应。
            <Link className="record-link" href="/member-imports?tab=mappings">
              先登记对应规则
              <ArrowRight size={14} />
            </Link>
          </p>
        )}
        {mapping && (
          <div className="record-table-scroll">
            <table className="record-table import-map-table">
              <caption className="sr-only">所选字段对应规则</caption>
              <thead>
                <tr>
                  <th>文件表头</th>
                  <th>导入字段</th>
                  <th>核对结果</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(mapping.columns).map(([column, field]) => (
                  <tr key={column}>
                    <td>{column}</td>
                    <td>{FIELD_LABEL[field] ?? "未识别字段"}</td>
                    <td>
                      {!content
                        ? "待选择文件"
                        : header.includes(column)
                          ? "已找到表头"
                          : "文件中缺少此列"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {content && mapping && missing.length > 0 && (
          <Banner tone="danger">文件缺少：{missing.join("、")}。请核对文件或改选对应规则。</Banner>
        )}
      </section>
      <section className="record-section">
        <h2>记录来源</h2>
        <Field label="来源依据">
          <Input
            required
            minLength={2}
            maxLength={200}
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="填写花名册名称、报送组织与日期"
          />
        </Field>
        <Field label="导入理由">
          <Textarea
            required
            minLength={4}
            maxLength={500}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <p className="record-muted">
          预检保存本批次的来源和逐行报告，会员档案在核对并确认入库后才会更新。
        </p>
      </section>
    </AccessAction>
  );
}
export function BatchEditor({
  report,
  action,
  onCancel,
  onSaved
}: {
  report: Report;
  action: "commit" | "discard";
  onCancel: () => void;
  onSaved: (result: unknown) => void;
}) {
  const [reason, setReason] = useState(""),
    [checked, setChecked] = useState(false),
    send = useImportCommand(),
    b = report.batch,
    ready = report.items.filter(
      (r) => r.outcome === "READY_NEW" || r.outcome === "READY_UPDATE"
    ).length;
  return (
    <AccessAction
      eyebrow="会员数据导入 · 批次办理"
      title={action === "commit" ? "确认批次入库" : "作废导入批次"}
      description={`${b.sourceRef} · ${b.mappingName}`}
      dirty={!!reason || checked}
      danger={action === "discard"}
      disabled={!checked || b.status !== "STAGED" || (action === "commit" && !ready)}
      label={action === "commit" ? "确认入库" : "确认作废"}
      onCancel={onCancel}
      onSaved={onSaved}
      inspect={async () => {
        const r = await api<Report>(`/api/v1/member-imports/${b.id}`);
        return [
          `${r.batch.sourceRef} · ${BATCH_LABEL[r.batch.status]} · ${batchCounts(r.batch)}`,
          `逐行记录：已入库 ${r.items.filter((row) => row.outcome === "APPLIED").length} 行，尚待处理 ${r.items.filter((row) => row.outcome !== "APPLIED").length} 行`
        ];
      }}
      submit={async () => {
        try {
          return await send(`/api/v1/member-imports/${b.id}/${action}`, {
            expectedVersion: b.version,
            reason: reason.trim()
          });
        } catch (error) {
          if (error instanceof ApiError && error.reasonCode === 'BLOCKING_DATA_ISSUES') {
            // This local presentation keeps the shared transport/error vocabulary untouched.
            throw new ApiError(error.status, '本批存在数据质量阻断，请先核对数据质量；本批未入库');
          }
          throw error;
        }
      }}
    >
      {action === "commit" && <ImportSteps step={3} />}
      <section className="record-section">
        <h2>核对处理范围</h2>
        <Facts>
          <Fact label="来源依据">{b.sourceRef}</Fact>
          <Fact label="预检时间">{formatTime(b.recordedAt)}</Fact>
          <Fact label="本批总行数">{b.rowCount} 行</Fact>
          <Fact label="本次可入库">{ready} 行</Fact>
        </Facts>
        <Banner tone="warning">
          {action === "commit"
            ? `只处理已通过预检的 ${ready} 行，其他 ${report.items.length - ready} 行保留在报告中。入库逐行执行，部分失败会保留原因；已写入的档案不能通过作废批次撤回。`
            : `本批次不再入库，原始行、预检结果和作废理由继续留存。`}
        </Banner>
        <Field label={action === "commit" ? "入库理由" : "作废理由"}>
          <Textarea
            required
            minLength={4}
            maxLength={500}
            rows={4}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setChecked(false);
            }}
          />
        </Field>
        <label className="record-check">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          我已核对本批次的处理范围与影响
        </label>
      </section>
    </AccessAction>
  );
}
